import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { TrackLocations } from "@/hooks/use-segment";
import {
    useClearTopupFailure,
    useGetBillingInfo,
    useGrantCredits,
    useOrganization,
    useReloadBillingInfo,
} from "@/queries/organization-queries";
import { TopupFailedPaymentBanner } from "@/routes/settings/organization/billing/FailedPaymentBanner";
import { OCUs } from "@/routes/settings/organization/billing/OCU";
import { formatError } from "@/utils/errors";
import { CreditStatus } from "gitpod-next-api/gitpod/v1/billing_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useRef, useState, type FC } from "react";
import { creditsToDollars, formatCredits } from "@/routes/settings/organization/billing/billing-utils";
import { Banner } from "@/components/flexkit/Banner";

export const CreditValueText: FC<{ credits: number; showDollarAmount?: boolean }> = ({
    credits,
    showDollarAmount = false,
}) => {
    const dollars = creditsToDollars(credits);
    return (
        <Text className="text-2xl font-semibold text-content-primary">
            {formatCredits(credits)}
            {showDollarAmount && (
                <span className="pl-2 text-md text-content-secondary">
                    {dollars > 0 ? `($${dollars.toFixed(2)})` : ""}
                </span>
            )}
        </Text>
    );
};

export const CreditBalance: FC = () => {
    const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);

    const { data: billingInfo, isPending: isLoadingBillingInfo } = useGetBillingInfo();
    const { data: organization, isPending: isPendingOrganization } = useOrganization();
    const { reload: reloadBillingInfo } = useReloadBillingInfo();

    const tier = organization?.tier;
    const isOnCoreTier = tier === OrganizationTier.CORE;

    // Poll for credits update
    // This is a workaround to avoid the issue where the credits are not updated immediately after a purchase
    // Polls every 5 seconds for up to 30 seconds
    // If the credits are updated, the polling will stop and show success toast
    const { toast } = useToast();
    const [isPolling, setIsPolling] = useState(false);
    const _currentTotalCredits = useRef(billingInfo?.totalCredits);
    useEffect(() => {
        _currentTotalCredits.current = billingInfo?.totalCredits ?? 0;
    }, [billingInfo]);
    const pollForCreditsUpdate = useCallback(() => {
        const initialTotalCredits = billingInfo?.totalCredits ?? 0;
        let pollCount = 0;
        const maxPolls = 6; // 6 polls * 5 seconds = 30 seconds
        setIsPolling(true);
        const interval = setInterval(() => {
            pollCount++;
            if (_currentTotalCredits.current !== initialTotalCredits) {
                clearInterval(interval);
                setIsPolling(false);
                toast({
                    title: "OCUs added!",
                    description: "Your OCUs are now available for use.",
                });
            } else if (pollCount >= maxPolls) {
                clearInterval(interval);
                setIsPolling(false);
            } else {
                reloadBillingInfo(false);
            }
        }, 5000);

        return () => {
            clearInterval(interval);
            setIsPolling(false);
        };
    }, [reloadBillingInfo, billingInfo, toast]);

    // If billing is not set up, don't show the card
    if (isLoadingBillingInfo || isPendingOrganization || !billingInfo) {
        return null;
    }

    const usagePercentage =
        billingInfo.totalCredits > 0 ? Math.round((billingInfo.availableCredits / billingInfo.totalCredits) * 100) : 0;
    const isOutOfCredits = billingInfo.creditStatus === CreditStatus.OUT_OF_CREDITS;
    const isLowOnCredits = billingInfo.creditStatus === CreditStatus.LOW_ON_CREDITS;

    return (
        <Card className="bg-surface-glass p-6" variant="bordered">
            <div className="mb-6">
                <h2 className="flex flex-row items-center text-lg font-semibold text-content-primary">
                    Ona Compute Units
                    {isPolling && <IconSpinner size={"sm"} className="ml-2 animate-spin" />}
                </h2>
                <Text className="text-sm text-content-secondary">
                    Ona Compute Units (OCUs) represent credits that are used for environment runtime and agent
                    conversations
                </Text>
            </div>

            <div className="mb-6 flex flex-wrap gap-x-8 gap-y-4">
                <div className="flex-grow">
                    <Text className="mb-1 text-sm text-content-secondary">Total Purchased</Text>
                    <CreditValueText credits={billingInfo.totalCredits} showDollarAmount />
                </div>
                <div className="flex-grow">
                    <Text className="mb-1 text-sm text-content-secondary">Available</Text>
                    <CreditValueText credits={billingInfo.availableCredits} />
                </div>
                <div className="flex-grow">
                    <Text className="mb-1 text-sm text-content-secondary">Used</Text>
                    <CreditValueText credits={billingInfo.usedCredits} />
                </div>
            </div>

            <div className="mb-6">
                <div className="h-4 w-full rounded-full border border-surface-04 bg-surface-glass p-0.5">
                    <div className="h-full overflow-hidden rounded-full bg-surface-glass">
                        <div
                            className="h-full rounded-full bg-gradient-to-r from-orange-100 to-orange-300 transition-all duration-300"
                            style={{ width: `${usagePercentage}%` }}
                        />
                    </div>
                </div>
            </div>

            {isOnCoreTier && (
                <Banner
                    variant="info"
                    text="Need more OCUs? Add credits any time."
                    action={{
                        text: "Add Credits",
                        onClick: () => setShowBuyCreditsModal(true),
                        "data-tracking-id": "add-credits-credit-balance",
                        variant: "primary",
                    }}
                />
            )}

            <TopupFailedPaymentBanner />

            {(isOutOfCredits || isLowOnCredits) && (
                <Banner
                    variant="warning"
                    text={
                        <>
                            {isOutOfCredits && <span>You&apos;ve run out of OCUs. </span>}
                            {isLowOnCredits && <span>You&apos;re running low on OCUs. </span>}
                            {isOnCoreTier && <span>Add credits now to stay on track.</span>}
                        </>
                    }
                />
            )}

            <TopUpModal
                open={showBuyCreditsModal}
                onClose={(success) => {
                    setShowBuyCreditsModal(false);
                    if (success) {
                        pollForCreditsUpdate();
                    }
                }}
            />
        </Card>
    );
};

const createCreditsItem = (credits: number) => {
    const dollars = creditsToDollars(credits);
    return {
        value: String(credits),
        label: `${credits} OCUs – $${dollars}`,
    };
};
const creditAmounts = [
    createCreditsItem(40),
    createCreditsItem(100),
    createCreditsItem(200),
    createCreditsItem(400),
    createCreditsItem(1000),
];

type TopUpModalProps = {
    open: boolean;
    onClose: (success?: boolean) => void;
};

const TopUpModal: FC<TopUpModalProps> = ({ open, onClose }) => {
    const [selectedAmount, setSelectedAmount] = useState<string>("100");
    const [showConfirmation, setShowConfirmation] = useState(false);
    const { toast } = useToast();
    const grantCreditsMutation = useGrantCredits();
    const clearTopupFailureMutation = useClearTopupFailure();

    const handleContinue = () => {
        setShowConfirmation(true);
    };

    const handlePurchase = async () => {
        try {
            // Clear any existing topup failure before attempting new purchase
            await clearTopupFailureMutation.mutateAsync();
            // Grant credits using the mutation
            await grantCreditsMutation.mutateAsync(Number(selectedAmount));

            toast({
                title: "Verifying payment",
                description: "We are verifying your top-up payment. This may take a minute.",
            });

            setShowConfirmation(false);
            onClose(true);
        } catch (error) {
            toast({
                title: "Unable to add OCUs",
                description: `${formatError(error)}. If the issue persists, try updating your payment method in the Stripe Portal below.`,
            });
        }
    };

    const handleClose = () => {
        setShowConfirmation(false);
        onClose();
    };

    const handleBackToSelection = () => {
        setShowConfirmation(false);
    };

    if (showConfirmation) {
        return (
            <TopUpConfirmationModal
                selectedAmount={Number(selectedAmount)}
                onClose={handleClose}
                onConfirm={handlePurchase}
                onBack={handleBackToSelection}
                isPending={grantCreditsMutation.isPending}
            />
        );
    }

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-md" data-track-location={TrackLocations.BillingModal}>
                <DialogHeader>
                    <DialogTitle>Add Credits</DialogTitle>
                    <DialogDescription>
                        Add <OCUs /> to continue using Ona. This one-time purchase is available immediately and
                        won&apos;t change your subscription.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <label htmlFor="amount-select" className="text-md font-medium text-content-primary">
                            Select amount
                        </label>
                        <Select value={selectedAmount} onValueChange={setSelectedAmount}>
                            <SelectTrigger id="amount-select" loading={false}>
                                <SelectValue placeholder="Select amount" />
                            </SelectTrigger>
                            <SelectContent>
                                {creditAmounts.map((amount) => (
                                    <SelectItem key={amount.value} value={amount.value} className="px-4 py-2 text-base">
                                        <div>{amount.label}</div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <Button variant="primary" onClick={handleContinue} data-tracking-id="continue-to-confirmation">
                        Continue
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type TopUpConfirmationModalProps = {
    selectedAmount: number;
    onClose: () => void;
    onConfirm: () => void;
    onBack: () => void;
    isPending: boolean;
};

const TopUpConfirmationModal: FC<TopUpConfirmationModalProps> = ({
    selectedAmount,
    onClose,
    onConfirm,
    onBack,
    isPending,
}) => {
    const dollars = creditsToDollars(selectedAmount);

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-lg" data-track-location={TrackLocations.BillingModal}>
                <DialogHeader>
                    <DialogTitle>Confirm your purchase</DialogTitle>
                    <DialogDescription className="space-y-4">
                        {/* Purchase Preview Section */}
                        <div className="border-border-secondary rounded-lg border bg-surface-secondary p-4">
                            <Text className="mb-3 font-semibold">Your purchase includes:</Text>

                            {/* Purchase Details */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Text className="text-content-secondary">OCUs</Text>
                                    <Text className="font-medium">{formatCredits(selectedAmount)}</Text>
                                </div>

                                <div className="flex items-center justify-between">
                                    <Text className="text-content-secondary">Amount</Text>
                                    <Text className="font-medium">${dollars.toFixed(2)}</Text>
                                </div>

                                <div className="border-border-secondary my-2 border-t" />

                                {/* Final pricing */}
                                <div className="flex items-center justify-between">
                                    <Text className="font-semibold">Total (before tax)</Text>
                                    <Text className="font-semibold">${dollars.toFixed(2)}</Text>
                                </div>
                            </div>
                        </div>

                        <Text className="text-sm text-content-secondary">
                            This one-time purchase will be charged to your default payment method. Tax will be
                            calculated based on your location.
                        </Text>
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter>
                    <Button variant="outline" onClick={onBack} disabled={isPending} data-tracking-id="back">
                        Back
                    </Button>
                    <Button
                        variant="primary"
                        onClick={onConfirm}
                        loading={isPending}
                        disabled={isPending}
                        data-tracking-id="confirm-purchase"
                    >
                        Confirm Purchase
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
